import {
  Campaign,
  PaymentsOutlined,
  Percent,
  Phone,
  PlaceOutlined,
  SavingsOutlined,
  SellOutlined,
  ShoppingBasketOutlined,
  ViewAgendaOutlined,
} from '@mui/icons-material';
import { ItemDetailsCard, Modal } from 'melp-design/components';
import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Discount, formatDiscountAmount } from 'store/discounts';
import { ModalProps } from 'store/modal';
import {
  formatCountry,
  formatList,
  formatNumber,
  formatPercents,
} from 'utils/format';
import { resolveTranslation } from 'utils/general';
import { useCompanyLanguages } from 'state/Administrators';

interface Props extends ModalProps {
  discount: Discount;
}

export const DiscountDetailsModal: FC<Props> = ({ discount, closeModal }) => {
  const { t, i18n } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();

  const translation = resolveTranslation(
    discount.translations,
    i18n.language,
    defaultLanguage,
  );
  const amount = (value: number | null) =>
    value == null ? '-' : formatNumber(i18n.language, value);
  const offerInfo = discount.type
    ? [
        {
          label: t('discounts.offerType.label'),
          icon: SellOutlined,
          content: t(`discounts.offerType.${discount.type}`),
        },
        ...(discount.type === 'PERCENTAGE'
          ? [
              {
                label: t('discounts.discountPercentage.label'),
                icon: Percent,
                content:
                  discount.discountedPercentage == null
                    ? '-'
                    : formatPercents(
                        i18n.language,
                        discount.discountedPercentage / 100,
                      ),
              },
            ]
          : []),
        ...(discount.type === 'FIXED'
          ? [
              {
                label: t('discounts.fixedAmount.label'),
                icon: PaymentsOutlined,
                content:
                  discount.discountedFixed == null
                    ? '-'
                    : formatDiscountAmount(
                        i18n.language,
                        discount.discountedFixed,
                        discount.currency,
                      ),
              },
            ]
          : []),
        {
          label: t('discounts.averageBasketSize.label'),
          icon: ShoppingBasketOutlined,
          content: amount(discount.averageBasketSize),
        },
        {
          label: t('discounts.averageSaving.label'),
          icon: SavingsOutlined,
          content:
            discount.averageSaving == null
              ? '-'
              : formatDiscountAmount(
                  i18n.language,
                  discount.averageSaving,
                  discount.currency,
                ),
        },
      ]
    : [];

  return (
    <Modal.Content
      plain
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal({ action: 'CLOSE' }),
          label: t('common.close'),
        },
      ]}
    >
      <ItemDetailsCard
        title={translation?.title}
        description={translation?.description}
        image={discount.image?.url}
        logo={discount.supplier?.logo?.url}
        tags={{
          items: [
            moment(discount.publishedAt).isAfter(moment().subtract(2, 'weeks'))
              ? t('common.new')
              : null,
            discount.priority <= 10 ? t('discounts.sponsored') : null,
            discount.validFrom
              ? t('alternatives.benefitValidFromDate', {
                  date: moment(discount.validFrom).format('l'),
                  interpolation: { escapeValue: false },
                })
              : null,
            discount.validTo
              ? t('alternatives.benefitValidToDate', {
                  date: moment(discount.validTo).format('l'),
                  interpolation: { escapeValue: false },
                })
              : null,
          ]
            .filter(Boolean)
            .map((tag) => ({
              label: tag,
            })),
        }}
        info={[
          ...offerInfo,
          {
            label: t('discounts.teaser.label'),
            icon: Campaign,
            content: translation?.teaser || '-',
          },
          {
            label: t('discounts.discountPromo.label'),
            icon: Campaign,
            content: translation?.promo || '-',
          },
          {
            label: t('benefits.country'),
            icon: PlaceOutlined,
            content: formatList(
              i18n.language,
              discount.countries.map((alpha3) =>
                formatCountry(i18n.language, alpha3),
              ),
            ),
          },
          {
            label: t('benefits.benefit_type'),
            icon: ViewAgendaOutlined,
            content: formatList(
              i18n.language,
              discount.categories.map((category) =>
                t(`marketplaceItems.itemCategory-${category}`),
              ),
            ),
          },
          {
            label: t('marketplaceItems.supplier'),
            icon: SellOutlined,
            content: discount.supplier?.name ?? '-',
          },
          ...(discount.supplier?.contact
            ? [
                {
                  label: t('discounts.supplierContact.label'),
                  icon: Phone,
                  content: discount.supplier.contact || '-',
                },
              ]
            : []),
        ]}
      />
    </Modal.Content>
  );
};
